import { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';
import {
  Box,
  Button,
  Alert,
  CircularProgress,
  Skeleton,
} from '@mui/material';
import { useForm, Controller, type FieldErrors } from 'react-hook-form';
import axios from 'axios';
import type { Provider, ProviderAssignment, EncounterResponse, Location } from '@claim-os/shared';
import type { SubmitClaimForm } from '../schema';
import { makeSubmitClaimResolver } from '../submitClaimResolver';
import ClaimTypeSection from '../components/ClaimTypeSection';
import type { ClaimTypeAssignment } from '@claim-os/shared';
import RequiredDocumentsList from '../components/RequiredDocumentsList';
import SuccessPage from '../components/SuccessPage';
import { submitEncounter } from '../services/submitClaimService';
import { toClaimTableRows } from '../../shared/utils/claimTableRows';
import {
  getProviders,
  getLocations,
  useAutoSelectSingleOption,
  useTenantSettings,
  resolveEncounterFieldsConfig,
  AppLayout,
  PageContent,
  FormSection,
  SectionHeading,
  FileUploadZone,
  PatientDetailsSection,
  EncounterInfoSection,
  ClaimBillTypeCard,
  ASA_CLASSIFICATION_OPTIONS,
  getProviderMode,
} from '@claim-os/shared';
import { CLAIM_TYPE_PROVIDER_CONFIG } from '@claim-os/shared';

const AVAILABLE_CLAIM_TYPES = Object.keys(CLAIM_TYPE_PROVIDER_CONFIG);

const EMPTY_CODE_ROW = { code: '', modifiers: [] as string[] };

/** A fresh claim-type entry: empty providers + seeded per-claim coding. ANESTHESIA
 *  gets one starter ASA code row; every claim gets one starter CPT row. */
function seedClaim(type: string) {
  return {
    type,
    providers: [] as ProviderAssignment[],
    coding: {
      icd10: [] as string[],
      cpt: [{ ...EMPTY_CODE_ROW }],
      asaClassification: '',
      // The window's dates default from Date of Service inside AnesthesiaTimeRange, so they
      // start blank here rather than being seeded with a date the biller never chose (044).
      anesthesiaStartDate: '',
      anesthesiaStartTime: '',
      anesthesiaEndDate: '',
      anesthesiaEndTime: '',
      asaCodes: type === 'ANESTHESIA'
        ? [{ ...EMPTY_CODE_ROW }]
        : ([] as { code: string; modifiers: string[] }[]),
    },
  };
}

export default function SubmitClaimPage() {
  const navigate = useNavigate();
  const [providers, setProviders] = useState<Provider[]>([]);
  const [providerLoading, setProviderLoading] = useState(true);
  const [providerError, setProviderError] = useState<string | null>(null);
  const [locations, setLocations] = useState<Location[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(true);
  const [locationsError, setLocationsError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [successResponse, setSuccessResponse] = useState<EncounterResponse | null>(null);

  // Feature 043 — practice settings drive which encounter fields render and whether
  // they're required. FR-018: a settings load failure falls back to the default (via
  // resolveEncounterFieldsConfig on the empty config), so a config outage never blocks
  // submission; the render is gated below only while the settings fetch is in flight.
  const { encounterFieldsConfig, claimTypes: tenantClaimTypes, isLoading: settingsLoading } = useTenantSettings();
  const config = useMemo(
    () => resolveEncounterFieldsConfig(encounterFieldsConfig),
    [encounterFieldsConfig],
  );
  const resolver = useMemo(
    () => makeSubmitClaimResolver(config.encounterFields, config.claimTypeFields),
    [config.encounterFields, config.claimTypeFields],
  );

  const form = useForm<SubmitClaimForm>({
    resolver,
    defaultValues: {
      firstName: '',
      lastName: '',
      dateOfBirth: '',
      medicalRecordNumber: '',
      insurer: '',
      memberId: '',
      claimTypes: [],
      dateOfService: '',
      location: '',
      isGapCase: undefined,
      documents: [],
    },
    mode: 'onChange',
  });

  useEffect(() => {
    let cancelled = false;
    setProviderLoading(true);
    getProviders()
      .then((data) => {
        if (cancelled) return;
        if (!Array.isArray(data)) throw new Error('Invalid response');
        setProviders(data);
      })
      .catch(() => { if (!cancelled) setProviderError('Could not retrieve providers. Please try again later.'); })
      .finally(() => { if (!cancelled) setProviderLoading(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLocationsLoading(true);
    getLocations()
      .then((data) => {
        if (cancelled) return;
        setLocations(data);
      })
      .catch(() => { if (!cancelled) setLocationsError('Could not retrieve locations. Please try again later.'); })
      .finally(() => { if (!cancelled) setLocationsLoading(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (submitting) {
        e.preventDefault();
        return '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [submitting]);

  const claimTypes = form.watch('claimTypes');
  const watchedDateOfService = form.watch('dateOfService');
  const selectedTypeCodes = claimTypes.map((c) => c.type);
  const isOfficeVisitOnly =
    claimTypes.length === 1 && claimTypes[0].type === 'OFFICE_VISIT';

  // Encounter field visibility/required — config AND claim-type gating combined
  // (gap case never applies to Office-Visit-only, regardless of config). Every field
  // is configurable (2026-07-16 amendment), including the structural anchors — a practice
  // can run a documents-only or name-anchored intake, backfilling the rest via the intake team.
  // `config.encounterFields` is passed straight through to PatientDetailsSection;
  // EncounterInfoSection needs the isGapCase override folded in below.
  const showGapCase = (config.encounterFields.isGapCase?.visible ?? true) && !isOfficeVisitOnly;
  const encounterInfoFields = {
    ...config.encounterFields,
    isGapCase: {
      visible: showGapCase,
      required: config.encounterFields.isGapCase?.required ?? true,
    },
  };

  // Structural anchors (feature 043): a not-visible anchor drops its whole section from
  // the form. Requiredness for these is enforced by the resolver/schema, not here.
  const documentsField = config.encounterFields.documents ?? { visible: true, required: true };
  const showBillType = config.encounterFields.billType?.visible ?? true;
  const showProviders = config.encounterFields.provider?.visible ?? true;

  // PatientDetailsSection / EncounterInfoSection self-hide (feature 043) when every
  // field they own is both not-visible AND empty — no page-level check needed here.

  // The tenant's enabled bill types (mirrors ClaimTypeSection's own filtering — an
  // unconfigured tenant, empty list, falls back to every catalog type). When there's
  // exactly one, the tile selector offers no real choice, so it's skipped in favor of
  // going straight to that type's billing card (Providers + Coding) — unless the card
  // itself would render nothing (no providers, no visible coding fields), in which case
  // the tile stays as the only indicator of which bill type this is.
  const effectiveClaimTypes =
    tenantClaimTypes.length === 0
      ? AVAILABLE_CLAIM_TYPES
      : AVAILABLE_CLAIM_TYPES.filter((code) => tenantClaimTypes.includes(code));
  const singleBillType = effectiveClaimTypes.length === 1 ? effectiveClaimTypes[0] : null;
  const singleBillTypeHasBillingContent =
    singleBillType != null &&
    ((showProviders && getProviderMode(singleBillType) !== 'none') ||
      Object.values(config.claimTypeFields[singleBillType] ?? {}).some((f) => f?.visible ?? true));
  // Also guards the brief window before the auto-select effect below has run (claims
  // start empty) — the selector stays visible until the type is actually selected,
  // instead of a one-frame flash of nothing.
  const matchesSingleBillType =
    singleBillType != null &&
    selectedTypeCodes.length > 0 &&
    selectedTypeCodes.every((code) => code === singleBillType);
  const skipBillTypeSelector =
    showBillType && singleBillType != null && singleBillTypeHasBillingContent && matchesSingleBillType;
  // The inverse of skipBillTypeSelector's content check: when the single bill type's
  // card would render nothing (no providers, no visible coding fields), suppress that
  // near-empty card so the tile selector — showing the type already selected — is the
  // only thing indicating which bill type this is, instead of the tile plus a bare
  // header-only card underneath it.
  const hideEmptySingleTypeCard = singleBillType != null && !singleBillTypeHasBillingContent && matchesSingleBillType;

  // A hidden gap-case field is never left unanswered (Office Visit OR config-hidden).
  useEffect(() => {
    if (!showGapCase) {
      form.setValue('isGapCase', false, { shouldValidate: true });
    }
  }, [showGapCase, form]);

  const handleClaimTypeToggle = useCallback((type: string) => {
    const current = form.getValues('claimTypes');
    const idx = current.findIndex((c) => c.type === type);
    const next =
      idx === -1
        ? [...current, seedClaim(type)]
        : [...current.slice(0, idx), ...current.slice(idx + 1)];
    form.setValue('claimTypes', next, { shouldValidate: true });
  }, [form]);

  // A claim's provider panel edits only its providers — preserve the claim's coding.
  const handleProvidersChange = useCallback(
    (code: string, next: ClaimTypeAssignment | null) => {
      const current = form.getValues('claimTypes');
      const updated = current.map((c) =>
        c.type === code ? { ...c, providers: next?.providers ?? [] } : c,
      );
      form.setValue('claimTypes', updated, { shouldValidate: true });
    },
    [form],
  );

  useAutoSelectSingleOption(
    effectiveClaimTypes,
    claimTypes.length === 0,
    useCallback(
      (code: string) => form.setValue('claimTypes', [seedClaim(code)], { shouldValidate: true }),
      [form],
    ),
  );

  const scrollToFirstError = useCallback(() => {
    requestAnimationFrame(() => {
      const el = document.querySelector('[aria-invalid="true"], .Mui-error');
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, []);

  const handleClaimSubmit = useCallback(async () => {
    const valid = await form.trigger();
    if (!valid) { scrollToFirstError(); return; }
    const data = form.getValues();

    setSubmitting(true);
    setSubmitError(null);

    try {
      const response = await submitEncounter(data, locations);
      setSuccessResponse(response);
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const status = err.response?.status;
        const errorData = err.response?.data as { error?: string; message?: string } | undefined;

        if (status === 400) {
          setSubmitError(errorData?.message ?? 'Validation failed. Please check your input.');
        } else if (status === 401 || status === 403) {
          setSubmitError('Your session has expired. Please log in again.');
        } else if (status === 429) {
          setSubmitError('Service is temporarily busy. Please try again in a few moments.');
        } else if (status === 502) {
          setSubmitError(errorData?.message ?? 'Submission failed. Please try again.');
        } else {
          setSubmitError(errorData?.message ?? 'Submission failed. Please try again.');
        }
      } else {
        setSubmitError('Submission failed. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }, [form, locations, scrollToFirstError]);

  const handleSubmitAnotherClaim = useCallback(() => {
    form.reset();
    setSuccessResponse(null);
    setSubmitError(null);
  }, [form]);

  const restartKey = useLocation().state?.restart as number | undefined;
  useEffect(() => {
    if (restartKey) handleSubmitAnotherClaim();
  }, [restartKey, handleSubmitAnotherClaim]);

  if (successResponse) {
    const formData = form.getValues();
    return (
      <AppLayout title="Submit A New Patient Encounter">
        <PageContent>
          <SuccessPage
            claimRows={toClaimTableRows(successResponse)}
            documentRows={successResponse.documents}
            patientFirstName={formData.firstName}
            patientLastName={formData.lastName}
            dateOfService={formData.dateOfService}
            onSubmitAnotherClaim={handleSubmitAnotherClaim}
          />
        </PageContent>
      </AppLayout>
    );
  }

  // Gate the form behind the settings load (FR-018): render once so the schema/resolver
  // and section props are built from the resolved config on the very first paint, instead
  // of rendering with defaults and re-rendering once settings arrive.
  if (settingsLoading) {
    return (
      <AppLayout title="Submit A New Patient Encounter" onBack={() => navigate('/')} backLabel="Claims">
        <PageContent width="form">
          {/* Patient Details */}
          <Skeleton variant="rounded" height={160} sx={{ mb: 3 }} />
          {/* Encounter Information */}
          <Skeleton variant="rounded" height={140} sx={{ mb: 3 }} />
          {/* Claim Types */}
          <Skeleton variant="rounded" height={140} sx={{ mb: 3 }} />
          {/* Upload Documents */}
          <Skeleton variant="rounded" height={120} sx={{ mb: 3 }} />
          {/* Submit / Cancel buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
            <Skeleton variant="rounded" width={100} height={36} />
            <Skeleton variant="rounded" width={100} height={36} />
          </Box>
        </PageContent>
      </AppLayout>
    );
  }

  return (
    <AppLayout
      title="Submit A New Patient Encounter"
      onBack={() => navigate('/')}
      backLabel="Claims"
    >
      <PageContent width="form">
        {/* Patient Details — self-hides (feature 043) when every field it owns is
            not-visible-and-empty. */}
        <PatientDetailsSection
          control={form.control}
          errors={form.formState.errors}
          fields={config.encounterFields}
        />

        {/* Encounter Information — self-hides (feature 043) when every field it owns is
            not-visible-and-empty. */}
        <EncounterInfoSection
          control={form.control}
          errors={form.formState.errors}
          locations={locations}
          locationsLoading={locationsLoading}
          locationsError={locationsError}
          fields={encounterInfoFields}
        />

        {/* Claim Types — pure on/off toggle tiles (Approach B). Hidden when the
            practice makes bill type not-visible (feature 043), or when there's only
            one enabled bill type and its billing card has content to show (no real
            choice to offer, so skip straight to that card). */}
        {showBillType && !skipBillTypeSelector && (
          <FormSection>
            <ClaimTypeSection
              selectedTypes={selectedTypeCodes}
              onToggle={handleClaimTypeToggle}
              subtitle="Toggle the claim types for this encounter. Each selected type adds its own coding section below."
              error={form.formState.errors.claimTypes?.message}
            />
          </FormSection>
        )}

        {/* One stacked bill-type card per selected claim type, in config order
            (stable regardless of toggle order). Each carries its own Providers + Coding. */}
        {AVAILABLE_CLAIM_TYPES.filter(
          (code) => selectedTypeCodes.includes(code) && !(hideEmptySingleTypeCard && code === singleBillType),
        ).map((code) => {
          const idx = claimTypes.findIndex((c) => c.type === code);
          if (idx < 0) return null;
          const claimTypeErrors = form.formState.errors.claimTypes;
          const entryErrors = Array.isArray(claimTypeErrors)
            ? (claimTypeErrors as Array<{ providers?: { message?: string }; coding?: FieldErrors<SubmitClaimForm> } | undefined>)[idx]
            : undefined;
          return (
            <Box key={code} sx={{ mb: 3 }}>
              <ClaimBillTypeCard
                claimType={code}
                onRemove={() => handleClaimTypeToggle(code)}
                assignment={claimTypes[idx]}
                onAssignmentChange={(next) => handleProvidersChange(code, next)}
                providers={providers}
                providersLoading={providerLoading}
                providersError={providerError}
                providerValidationError={entryErrors?.providers?.message}
                control={form.control}
                fieldPrefix={`claimTypes.${idx}.coding`}
                codingErrors={entryErrors?.coding}
                asaOptions={ASA_CLASSIFICATION_OPTIONS}
                dateOfService={watchedDateOfService}
                fields={config.claimTypeFields[code]}
                showProviders={showProviders}
              />
            </Box>
          );
        })}

        {/* Upload Documents — hidden when the practice makes documents not-visible (feature 043). */}
        {documentsField.visible && (
          <FormSection>
            <SectionHeading
              title="Upload Documents"
              subtitle="Upload the relevant documents according to the required documents listed in the checklist above."
            />
            <RequiredDocumentsList selectedTypeCodes={selectedTypeCodes} />
            <Box sx={{ mt: 3 }}>
              <Controller
                name="documents"
                control={form.control}
                render={({ field }) => (
                  <FileUploadZone
                    files={field.value ?? []}
                    onChange={field.onChange}
                    label=""
                    required={documentsField.required}
                    error={form.formState.errors.documents?.message}
                  />
                )}
              />
            </Box>
          </FormSection>
        )}

        {submitError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {submitError}
          </Alert>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
          <Button variant="outlined" onClick={() => navigate('/')} sx={{ height: 36 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleClaimSubmit}
            disabled={submitting}
            startIcon={
              submitting ? <CircularProgress size={16} color="inherit" /> : undefined
            }
            color="primary"
            sx={{ height: 36 }}
          >
            {submitting ? 'Uploading…' : 'Submit'}
          </Button>
        </Box>
      </PageContent>
    </AppLayout>
  );
}
